import React, { FC, useLayoutEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { SignInPage } from '../common/pages/sign-in-page'
import { PasswordResetPage } from '../common/pages/password-reset-page'
import { ResetPasswordRequestPage } from '../common/pages/reset-password-request-page'
import { ResetPasswordRequestSuccessPage } from '../common/pages/reset-password-request-success-page'
import { ResetPasswordRequestErrorPage } from '../common/pages/reset-password-request-error-page'
import { usePortalSession } from '../common/hooks/usePortalSession'
import { PortalSessionLoader } from '../common/components/portal-session-loader'
import type { PortalSessionStatus } from '../common/hooks/portalSessionState'

import { UserAppDashboardPage } from './pages/dashboard-page'
import { UserAgreementPage } from './pages/agreement-page'
import { SignUpPage } from './pages/sign-up-page'
import { UserAppRegisterChargepoint } from './pages/register-chargepoint-page'
import { UserAppRemoteControlPage } from './pages/remote-control-page'
import { UserAppProfilePage } from './pages/profile-page'
import { UserAppSupportPage } from './pages/support-page'
import { UserAppStatisticPage } from './pages/statistic-page'
import { UserAppSchedulePage } from './pages/schedule-page'
import { UserAppPageHeader } from './components/userapp-page-header'

type RequireAuthProps = {
    children?: JSX.Element | React.ReactNode
    isHeaderShown?: boolean
    sessionStatus: PortalSessionStatus
}

const RequireAuth: FC<RequireAuthProps> = ({ children, isHeaderShown = true, sessionStatus }) => {
    const location = useLocation()

    if (sessionStatus === 'loading') {
        return <PortalSessionLoader />
    }

    return sessionStatus === 'authenticated' ? (
        <div>
            {isHeaderShown && (
                <div className="page-layout-header py-3">
                    <UserAppPageHeader />
                </div>
            )}
            {children}
        </div>
    ) : (
        <Navigate to="/login" state={{ from: location.pathname }} replace />
    )
}

const App = () => {
    const sessionStatus = usePortalSession()

    const location = useLocation()

    useLayoutEffect(() => {
        // Scroll to the top of the page when the route changes
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }, [location.pathname])

    return (
        <Routes>
            <Route
                path="/*"
                element={
                    sessionStatus === 'loading' ? (
                        <PortalSessionLoader />
                    ) : (
                        <Navigate to={sessionStatus === 'authenticated' ? '/dashboard' : '/login'} />
                    )
                }
            />
            <Route path="login" element={<SignInPage />} />
            <Route path="reset-password" element={<PasswordResetPage />} />
            <Route path="user-agreement" element={<UserAgreementPage />} />
            <Route path="reset-password-request" element={<ResetPasswordRequestPage />} />
            <Route path="reset-password-request-success" element={<ResetPasswordRequestSuccessPage />} />
            <Route path="reset-password-request-error" element={<ResetPasswordRequestErrorPage />} />
            <Route path="register-user" element={<SignUpPage />} />
            <Route
                path="dashboard"
                element={
                    <RequireAuth sessionStatus={sessionStatus}>
                        <UserAppDashboardPage />
                    </RequireAuth>
                }
            />
            <Route
                path="registerchargepoint"
                element={
                    <RequireAuth isHeaderShown={false} sessionStatus={sessionStatus}>
                        <UserAppRegisterChargepoint />
                    </RequireAuth>
                }
            />

            <Route
                path="remotecontrol"
                element={
                    <RequireAuth sessionStatus={sessionStatus}>
                        <UserAppRemoteControlPage />
                    </RequireAuth>
                }
            />
            <Route
                path="remotecontrol/:chargePointId/schedule"
                element={
                    <RequireAuth sessionStatus={sessionStatus}>
                        <UserAppSchedulePage />
                    </RequireAuth>
                }
            />

            <Route
                path="user-profile"
                element={
                    <RequireAuth sessionStatus={sessionStatus}>
                        <UserAppProfilePage />
                    </RequireAuth>
                }
            />
            <Route
                path="support"
                element={
                    <RequireAuth sessionStatus={sessionStatus}>
                        <UserAppSupportPage />
                    </RequireAuth>
                }
            />
            <Route
                path="statistics"
                element={
                    <RequireAuth sessionStatus={sessionStatus}>
                        <UserAppStatisticPage />
                    </RequireAuth>
                }
            />
        </Routes>
    )
}

export default App
